import type { CSSProperties } from "react";
import { useEffect, useId, useState } from "react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Database,
  MessageCircle,
  Route,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import dentalClinicsImage from "../assets/industries/dental-clinics.jpg";
import educationTrainingImage from "../assets/industries/education-training.jpg";
import fieldServicesImage from "../assets/industries/field-services.jpg";
import fitnessWellnessImage from "../assets/industries/fitness-wellness.jpg";
import hospitalityRentalsImage from "../assets/industries/hospitality-rentals.jpg";
import localServicesImage from "../assets/industries/local-services.jpg";
import medicalAestheticsImage from "../assets/industries/medical-aesthetics.jpg";
import petServicesImage from "../assets/industries/pet-services.jpg";
import workOrderServicesImage from "../assets/industries/work-order-services.jpg";
import { industryPageDesigns, type IndustryVisualFamily, type IndustryWorkflowScenario } from "../industryPageDesign";
import { getIndustrySeoDocument, getIndustrySolution, type IndustrySlug } from "../industrySolutions";
import { demoPath, leadFormPath } from "../routes";
import { reportLeadEntryConversion } from "../tracking";
import { Footer } from "./Footer";
import { DentalClinicsPage } from "./DentalClinicsPage";
import { Nav } from "./Nav";
import { NotFoundPage } from "./NotFoundPage";
import { Seo } from "./Seo";

const industryHeroImages: Record<IndustrySlug, string> = {
  "medical-aesthetics": medicalAestheticsImage,
  "dental-clinics": dentalClinicsImage,
  "pet-services": petServicesImage,
  "education-training": educationTrainingImage,
  "field-services": fieldServicesImage,
  "hospitality-rentals": hospitalityRentalsImage,
  "fitness-wellness": fitnessWellnessImage,
  "local-services": localServicesImage,
  "work-order-services": workOrderServicesImage,
};

const familyTheme: Record<IndustryVisualFamily, { accent: string; accentSoft: string; surface: string; status: string }> = {
  acquisition: { accent: "#6430c8", accentSoft: "#eee7fa", surface: "#f6f1fa", status: "#1f9d73" },
  operations: { accent: "#3658b7", accentSoft: "#e8edfb", surface: "#f1f4fa", status: "#188f7b" },
  service: { accent: "#a34e52", accentSoft: "#f7e9e7", surface: "#f8f1ee", status: "#277f6e" },
};

const workflowReviewSteps = [
  { number: "01", title: "Required fields", body: "Confirm exactly what the workflow must collect before it can move forward.", icon: ClipboardCheck },
  { number: "02", title: "Approved action", body: "Define what ClawEase may prepare, read, write, or send—and what it may not.", icon: Database },
  { number: "03", title: "Human handoff", body: "Set the conditions, owner, and context passed when a person needs to take over.", icon: UserRoundCheck },
];

function addSource(path: string, source: string) {
  const url = new URL(path, "https://clawease.ai");
  url.searchParams.set("source", source);
  return `${url.pathname}${url.search}${url.hash}`;
}

function ConversationCard({ scenario }: { scenario: IndustryWorkflowScenario }) {
  return (
    <section className="min-w-0 rounded-[18px] border border-[#e4dde8] bg-[#fcfbfd] p-4 sm:p-5" aria-label="Sample conversation">
      <div className="flex items-center gap-2 text-[12px] font-bold text-[#685f6c]">
        <MessageCircle className="h-4 w-4" />
        Customer conversation
      </div>
      <div className="mt-5 space-y-3">
        <div className="max-w-[92%] rounded-[15px] rounded-tl-[5px] bg-[#28143f] px-4 py-3 text-[13px] font-medium leading-5 text-white/[.92]">
          {scenario.inquiry}
        </div>
        <div className="ml-auto max-w-[94%] rounded-[15px] rounded-tr-[5px] border border-[#e0d6e7] bg-white px-4 py-3 text-[13px] font-medium leading-5 text-[#514a55]">
          {scenario.response}
        </div>
      </div>
      <div className="mt-5 flex items-center gap-2 border-t border-[#e8e1eb] pt-4 text-[12px] font-semibold text-[#776f7b]">
        <span className="h-2 w-2 rounded-full bg-[var(--industry-accent)]" />
        Answer source and permitted claims are approved during setup.
      </div>
    </section>
  );
}

function ProfileCard({ scenario }: { scenario: IndustryWorkflowScenario }) {
  return (
    <section className="min-w-0 rounded-[18px] border border-[#e4dde8] bg-white p-4 sm:p-5" aria-label="Structured workflow record">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[12px] font-bold text-[#685f6c]">
          <ClipboardCheck className="h-4 w-4 text-[var(--industry-accent)]" />
          Structured record
        </div>
        <span className="rounded-full bg-[var(--industry-soft)] px-2.5 py-1 text-[11px] font-bold text-[var(--industry-accent)]">Sample data</span>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-5">
        {scenario.fields.map((field) => (
          <div key={field.label} className="min-w-0 border-b border-[#ece7ee] pb-3">
            <div className="text-[11px] font-semibold text-[#8a818f]">{field.label}</div>
            <div className="mt-1.5 break-words text-[13px] font-bold leading-5 text-[#2e2931]">{field.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-[13px] bg-[#f7f4f8] px-4 py-3 text-[12px] font-medium leading-5 text-[#6c6470]">
        Required and optional fields are configured for one approved request type at a time.
      </div>
    </section>
  );
}

function NextStepCard({ scenario }: { scenario: IndustryWorkflowScenario }) {
  return (
    <section className="min-w-0 rounded-[18px] bg-[#28143f] p-4 text-white sm:p-5" aria-label="Prepared next step">
      <div className="flex items-center gap-2 text-[12px] font-bold text-white/[.62]">
        <Route className="h-4 w-4" />
        {scenario.nextStep.label}
      </div>
      <div className="mt-8 flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
        <CheckCircle2 className="h-5 w-5 text-[#7cdfb8]" />
      </div>
      <h3 className="mt-4 text-[22px] font-bold leading-7 tracking-[-.025em]">{scenario.nextStep.title}</h3>
      <p className="mt-2 text-[13px] font-medium leading-5 text-white/[.62]">{scenario.nextStep.detail}</p>
      <div className="mt-7 border-t border-white/[.12] pt-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-bold text-white/[.82]">
          <span className="h-2 w-2 rounded-full bg-[#79d9b5]" />
          {scenario.nextStep.status}
        </span>
      </div>
    </section>
  );
}

function WorkflowStudio({ scenarios, activeIndex, onChange, family }: { scenarios: IndustryWorkflowScenario[]; activeIndex: number; onChange: (index: number) => void; family: IndustryVisualFamily }) {
  const tabId = useId();
  const activeScenario = scenarios[activeIndex];

  return (
    <div className="mt-10 grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-6">
      <div className="flex gap-2 overflow-x-auto pb-1 lg:block lg:space-y-2 lg:overflow-visible lg:pb-0" role="tablist" aria-label="Sample workflow scenarios">
        {scenarios.map((scenario, index) => {
          const selected = index === activeIndex;
          return (
            <button
              key={scenario.id}
              id={`${tabId}-tab-${scenario.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${tabId}-panel-${scenario.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(index)}
              className={`group flex min-h-12 min-w-[164px] items-center justify-between gap-4 rounded-[14px] px-4 py-3 text-left text-[13px] font-bold transition lg:w-full lg:min-w-0 ${
                selected ? "bg-[#28143f] text-white shadow-[0_12px_30px_rgba(40,20,63,.14)]" : "border border-[#e2dbe5] bg-white text-[#4f4853] hover:border-[var(--industry-accent)]"
              }`}
            >
              <span>{scenario.tab}</span>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${selected ? "bg-white/[.12]" : "bg-[var(--industry-soft)] text-[var(--industry-accent)]"}`}>
                {selected ? <Check className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
              </span>
            </button>
          );
        })}
        <div className="hidden rounded-[14px] border border-dashed border-[#d9cfdf] p-4 text-[12px] font-medium leading-5 text-[#756d79] lg:mt-4 lg:block">
          Select a scenario. The panel changes only when you choose a tab.
        </div>
      </div>

      <div
        key={activeScenario.id}
        id={`${tabId}-panel-${activeScenario.id}`}
        role="tabpanel"
        aria-labelledby={`${tabId}-tab-${activeScenario.id}`}
        className="overflow-hidden rounded-[24px] border border-[#ddd4e2] bg-white shadow-[0_20px_60px_rgba(42,18,62,.10)]"
      >
        <header className="flex flex-col gap-3 border-b border-[#e8e1eb] bg-[#fcfbfd] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <div className="text-[12px] font-bold text-[var(--industry-accent)]">Sample workflow · Fictional example</div>
            <h3 className="mt-1 text-[19px] font-bold tracking-[-.02em] text-[#242027]">{activeScenario.title}</h3>
          </div>
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[var(--industry-soft)] px-3 py-1.5 text-[11px] font-bold text-[var(--industry-accent)]">
            <span className="h-2 w-2 rounded-full bg-[var(--industry-status)]" />
            {family === "operations" ? "Request-to-record" : family === "service" ? "Context-to-service" : "Inquiry-to-next-step"}
          </span>
        </header>

        <div className="grid gap-3 bg-[var(--industry-surface)] p-3 sm:p-4 xl:grid-cols-[.95fr_1.1fr_.82fr]">
          <ConversationCard scenario={activeScenario} />
          <ProfileCard scenario={activeScenario} />
          <NextStepCard scenario={activeScenario} />
        </div>

        <footer className="flex items-start gap-3 border-t border-[#e8e1eb] bg-white px-5 py-4 sm:px-6">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--industry-accent)]" />
          <div>
            <div className="text-[12px] font-bold text-[#302a34]">Human review rule</div>
            <p className="mt-1 text-[12px] font-medium leading-5 text-[#716976]">{activeScenario.humanReview}</p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export function IndustrySolutionPage({ industrySlug }: { industrySlug?: IndustrySlug }) {
  const params = useParams<{ industrySlug: string }>();
  const location = useLocation();
  const industry = getIndustrySolution(industrySlug || params.industrySlug);
  const [activeScenario, setActiveScenario] = useState(0);
  const lang = "en" as const;

  useEffect(() => setActiveScenario(0), [industry?.slug]);

  if (!industry) return <NotFoundPage defaultLang={lang} />;

  const content = industry.copy.en;
  const design = industryPageDesigns[industry.slug];
  const theme = familyTheme[design.family];
  const Icon = industry.icon;
  const heroImage = industryHeroImages[industry.slug];
  const seo = getIndustrySeoDocument(industry);

  if (industry.slug === "dental-clinics") {
    return <DentalClinicsPage heroImage={heroImage} seo={seo} />;
  }

  const contactPath = (placement: "hero" | "final") => leadFormPath(lang, undefined, `industry_${industry.slug}_${placement}`, location.search);
  const samplePath = addSource(demoPath(lang, location.search), `industry_${industry.slug}_sample`);
  const themeStyle = {
    "--industry-accent": theme.accent,
    "--industry-soft": theme.accentSoft,
    "--industry-surface": theme.surface,
    "--industry-status": theme.status,
  } as CSSProperties;

  return (
    <div className="min-h-screen bg-[#fcfbfd] font-sans text-[#17131b]" style={themeStyle}>
      <Seo lang={lang} document={seo} />
      <Nav lang={lang} />

      <main>
        <section className="relative overflow-hidden px-5 pb-14 pt-28 sm:px-10 lg:pb-20 lg:pt-32">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[760px] bg-[radial-gradient(circle_at_8%_8%,rgba(112,64,195,.10),transparent_32%),radial-gradient(circle_at_92%_10%,rgba(200,53,181,.07),transparent_29%)]" />
          <div className="relative mx-auto grid max-w-[1240px] items-center gap-10 lg:grid-cols-[.95fr_1.05fr] lg:gap-12">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[var(--industry-soft)] px-3 py-1.5 text-[12px] font-bold text-[var(--industry-accent)]">
                <Icon className="h-4 w-4" />
                {content.name}
              </div>
              <h1 className="mt-5 max-w-[680px] text-[40px] font-bold leading-[1.08] tracking-[-.038em] sm:text-[52px] lg:text-[56px] lg:leading-[1.05]">
                {design.headline}
              </h1>
              <p className="mt-5 max-w-[650px] text-[16px] font-medium leading-7 text-[#625a66] sm:text-[17px] sm:leading-8">{design.description}</p>
              <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <Link
                  to={contactPath("hero")}
                  onClick={() => reportLeadEntryConversion(location.search)}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#28143f] px-6 text-[13px] font-bold text-white no-underline shadow-[0_14px_34px_rgba(40,20,63,.17)] transition hover:-translate-y-0.5 hover:bg-[#402062]"
                >
                  {design.primaryCta}<ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#sample-workflow" className="inline-flex min-h-11 items-center gap-2 text-[13px] font-bold text-[#4c4450] no-underline hover:text-[var(--industry-accent)]">
                  {design.secondaryCta}<ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-3 text-[12px] font-medium leading-5 text-[#837a88]">Opens a short request form. It does not book a calendar appointment.</p>
              <ul className="mt-7 grid gap-3 sm:grid-cols-3">
                {design.capabilities.map((capability) => (
                  <li key={capability} className="flex items-start gap-2 text-[12px] font-semibold leading-5 text-[#5f5763]">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--industry-accent)]" />
                    {capability}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mx-auto w-full max-w-[690px] pb-11 sm:pb-14">
              <figure className="aspect-[1.32/1] overflow-hidden rounded-[28px] bg-[#e9e3ec] shadow-[0_24px_65px_rgba(42,18,62,.13)]">
                <img src={heroImage} alt={`${content.name} team in an industry setting`} className="h-full w-full object-cover" loading="eager" fetchPriority="high" />
              </figure>
              <aside className="absolute -bottom-1 left-4 right-4 rounded-[18px] border border-white/80 bg-white/[.88] p-4 shadow-[0_18px_48px_rgba(43,22,58,.18)] backdrop-blur-xl sm:left-auto sm:right-7 sm:w-[430px] sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[#28143f] text-white"><Route className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold text-[var(--industry-accent)]">Sample workflow · Fictional example</div>
                      <div className="mt-1 truncate text-[14px] font-bold text-[#2b252e]">{design.scenarios[0].tab}</div>
                    </div>
                  </div>
                  <span className="rounded-full bg-[#e8f6f1] px-2.5 py-1 text-[11px] font-bold text-[#247d61]">Structured</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {["Conversation", "Record", "Next step"].map((step, index) => (
                    <div key={step} className="rounded-[10px] bg-[#f6f3f7] px-2 py-2.5 text-center">
                      <div className={`mx-auto flex h-5 w-5 items-center justify-center rounded-full ${index === 2 ? "bg-[#dff3eb] text-[#267e63]" : "bg-[var(--industry-soft)] text-[var(--industry-accent)]"}`}>
                        {index === 2 ? <Check className="h-3 w-3" /> : <span className="text-[10px] font-bold">{index + 1}</span>}
                      </div>
                      <span className="mt-1.5 block text-[10px] font-semibold text-[#716876]">{step}</span>
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          </div>

          <div className="relative mx-auto mt-14 grid max-w-[1240px] overflow-hidden rounded-[20px] border border-[#e3dce6] bg-white shadow-[0_12px_34px_rgba(42,18,62,.06)] md:grid-cols-3">
            {design.proof.map((item, index) => (
              <article key={item.label} className={`p-5 sm:p-6 ${index ? "border-t border-[#e7e1e9] md:border-l md:border-t-0" : ""}`}>
                <div className="text-[12px] font-bold text-[var(--industry-accent)]">{item.label}</div>
                <h2 className="mt-2 text-[18px] font-bold tracking-[-.02em]">{item.title}</h2>
                <p className="mt-2 text-[13px] font-medium leading-6 text-[#6e6672]">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="sample-workflow" className="scroll-mt-28 bg-[#f5f1f7] px-5 py-16 sm:px-10 lg:py-24">
          <div className="mx-auto max-w-[1240px]">
            <div className="grid gap-5 lg:grid-cols-[.9fr_1.1fr] lg:items-end">
              <div>
                <p className="text-[12px] font-bold text-[var(--industry-accent)]">{design.workflowEyebrow}</p>
                <h2 className="mt-4 max-w-[680px] text-[34px] font-bold leading-[1.08] tracking-[-.035em] sm:text-[46px]">{design.workflowTitle}</h2>
              </div>
              <p className="max-w-[630px] text-[15px] font-medium leading-7 text-[#6a626e] lg:justify-self-end">{design.workflowBody}</p>
            </div>

            <WorkflowStudio scenarios={design.scenarios} activeIndex={activeScenario} onChange={setActiveScenario} family={design.family} />

            <div className="mt-6 flex flex-col gap-4 rounded-[18px] border border-[#ddd4e2] bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="max-w-[720px] text-[13px] font-medium leading-6 text-[#665e6a]">Want to feel the interaction? The current public demo uses a fictional dental clinic so it never creates a real appointment.</p>
              <Link to={samplePath} className="inline-flex shrink-0 items-center gap-2 text-[13px] font-bold text-[var(--industry-accent)] no-underline hover:underline">
                Try the interactive sample<ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-16 sm:px-10 lg:py-24">
          <div className="mx-auto max-w-[1180px]">
            <div className="grid gap-12 lg:grid-cols-[.92fr_1.08fr] lg:gap-16">
              <div>
                <p className="text-[12px] font-bold text-[var(--industry-accent)]">Workflow review</p>
                <h2 className="mt-4 text-[34px] font-bold leading-[1.08] tracking-[-.035em] sm:text-[45px]">Validate one workflow before expanding.</h2>
                <p className="mt-5 max-w-[560px] text-[15px] font-medium leading-7 text-[#6b636f]">Start with one repeated request. Make the fields, permitted actions, and human boundaries explicit before adding more channels or systems.</p>
                <div className="mt-8 border-t border-[#e3dce6]">
                  {workflowReviewSteps.map(({ number, title, body, icon: StepIcon }) => (
                    <article key={number} className="grid grid-cols-[42px_1fr] gap-4 border-b border-[#e3dce6] py-5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-[11px] bg-[var(--industry-soft)] text-[var(--industry-accent)]"><StepIcon className="h-4 w-4" /></span>
                      <div><div className="text-[11px] font-bold text-[#918797]">{number}</div><h3 className="mt-1 text-[17px] font-bold">{title}</h3><p className="mt-1.5 text-[13px] font-medium leading-6 text-[#6f6773]">{body}</p></div>
                    </article>
                  ))}
                </div>
              </div>

              <div className="grid gap-5">
                <article className="rounded-[22px] bg-[#f5f1f7] p-6 sm:p-8">
                  <div className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-white text-[var(--industry-accent)] shadow-[0_8px_22px_rgba(42,18,62,.07)]"><ClipboardCheck className="h-5 w-5" /></div>
                  <h3 className="mt-6 text-[25px] font-bold tracking-[-.025em]">What to measure in a pilot</h3>
                  <p className="mt-3 text-[13px] font-medium leading-6 text-[#6c6470]">Agree on the baseline, event definition, and review cadence first. These are measurement areas—not promised results.</p>
                  <ul className="mt-6 grid gap-3">
                    {design.pilotSignals.map((signal) => <li key={signal} className="flex items-center gap-3 rounded-[13px] bg-white px-4 py-3 text-[13px] font-bold text-[#403944]"><CheckCircle2 className="h-4 w-4 shrink-0 text-[var(--industry-status)]" />{signal}</li>)}
                  </ul>
                </article>

                <article className="rounded-[22px] bg-[#28143f] p-6 text-white sm:p-8">
                  <ShieldCheck className="h-6 w-6 text-[#d8c4ef]" />
                  <h3 className="mt-5 text-[25px] font-bold tracking-[-.025em]">Human control stays visible</h3>
                  <p className="mt-3 text-[13px] font-medium leading-6 text-white/[.62]">A person takes over when the request reaches an approved boundary or the customer asks for help.</p>
                  <ul className="mt-6 space-y-3">
                    {design.handoffRules.map((rule) => <li key={rule} className="flex items-start gap-3 text-[13px] font-semibold leading-6 text-white/[.82]"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#82ddba]" />{rule}</li>)}
                  </ul>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#f5f1f7] px-5 py-16 sm:px-10 lg:py-20">
          <div className="mx-auto grid max-w-[1080px] gap-9 lg:grid-cols-[.7fr_1.3fr]">
            <div>
              <p className="text-[12px] font-bold text-[var(--industry-accent)]">Before you start</p>
              <h2 className="mt-4 text-[34px] font-bold leading-[1.08] tracking-[-.035em] sm:text-[42px]">Questions a serious pilot should answer.</h2>
            </div>
            <div className="border-t border-[#dcd3e1]">
              {[
                ["Is the workflow shown here connected to a real business?", "No. It is a clearly labeled fictional sample used to explain the structure. Production fields, permissions, data sources, and actions must be reviewed before setup."],
                ["Does ClawEase replace our current system?", "The intended role is to coordinate an approved request across your existing process. Exact connections and write permissions must be validated for your environment."],
                ["Can we start with one request type?", "Yes. A focused pilot should begin with one repeated request, one defined record, one permitted action, and explicit human handoff rules."],
                ["What happens when the workflow cannot continue safely?", "It stops the automated path and passes the collected context to the approved owner instead of improvising an answer or action."],
              ].map(([question, answer]) => (
                <details key={question} className="group border-b border-[#dcd3e1] py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-[15px] font-bold leading-6"><span>{question}</span><span className="text-xl font-normal text-[var(--industry-accent)] transition group-open:rotate-45">+</span></summary>
                  <p className="max-w-[690px] pb-1 pt-4 text-[14px] font-medium leading-7 text-[#6d6571]">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-16 sm:px-10 lg:py-24">
          <div className="relative mx-auto max-w-[1160px] overflow-hidden rounded-[26px] bg-[#28143f] px-7 py-12 text-white shadow-[0_24px_65px_rgba(40,20,63,.18)] sm:px-11 lg:px-14 lg:py-14">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#8a52dc]/20 blur-3xl" />
            <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <p className="text-[12px] font-bold text-[#cfb7ec]">Start with one workflow</p>
                <h2 className="mt-4 max-w-[760px] text-[34px] font-bold leading-[1.08] tracking-[-.035em] sm:text-[45px]">{design.finalTitle}</h2>
                <p className="mt-4 max-w-[690px] text-[14px] font-medium leading-7 text-white/[.64]">{design.finalBody}</p>
              </div>
              <div className="lg:text-right">
                <Link
                  to={contactPath("final")}
                  onClick={() => reportLeadEntryConversion(location.search)}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white px-6 text-[13px] font-bold text-[#32194d] no-underline transition hover:-translate-y-0.5"
                >
                  {design.primaryCta}<ArrowRight className="h-4 w-4" />
                </Link>
                <p className="mt-3 text-[11px] font-medium text-white/[.46]">Request form · No calendar booking implied</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer lang={lang} compact />
    </div>
  );
}
